import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface FriendEngagement {
  friendsWhoLiked: string[];
  friendsWhoCommented: string[];
  friendsWhoSupported: string[];
  totalFriends: number;
}

export const useFriendEngagement = (postId: string) => {
  const [friendEngagement, setFriendEngagement] = useState<FriendEngagement>({
    friendsWhoLiked: [],
    friendsWhoCommented: [],
    friendsWhoSupported: [],
    totalFriends: 0
  });

  useEffect(() => {
    const fetchFriendEngagement = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Get user's friends
        const { data: friends } = await supabase.rpc('get_user_friends', {
          user_id: user.id
        });

        const friendIds = friends?.map(f => f.friend_id) || [];
        setFriendEngagement(prev => ({ ...prev, totalFriends: friendIds.length }));

        if (friendIds.length === 0) return;

        // Get friends who liked this post
        const { data: friendLikes } = await supabase
          .from('post_reactions')
          .select('user_id, profiles!inner(username)')
          .eq('post_id', postId)
          .eq('reaction_type', 'like')
          .in('user_id', friendIds);

        // Get friends who commented on this post
        const { data: friendComments } = await supabase
          .from('comments')
          .select('user_id, profiles!inner(username)')
          .eq('post_id', postId)
          .in('user_id', friendIds);

        // Get friends who supported this post
        const { data: friendSupports } = await supabase
          .from('post_reactions')
          .select('user_id, profiles!inner(username)')
          .eq('post_id', postId)
          .eq('reaction_type', 'support')
          .in('user_id', friendIds);

        setFriendEngagement({
          friendsWhoLiked: friendLikes?.map(f => f.profiles.username) || [],
          friendsWhoCommented: [...new Set(friendComments?.map(f => f.profiles.username) || [])],
          friendsWhoSupported: friendSupports?.map(f => f.profiles.username) || [],
          totalFriends: friendIds.length
        });

      } catch (error) {
        console.error('Error fetching friend engagement:', error);
      }
    };

    fetchFriendEngagement();
  }, [postId]);

  return friendEngagement;
};